import { useState, type ReactNode } from "react";
import { AlertTriangle, ChevronRight, Wrench } from "lucide-react";

import { cn } from "@/lib/utils";

/** A single tool/function invocation surfaced for debugging in the test UIs. */
export type ToolCallData = {
  name: string;
  arguments?: unknown;
  result?: unknown;
  duration_ms?: number | null;
  error?: string | null;
  /** Voice: the call has been announced but the result hasn't arrived yet. */
  pending?: boolean;
};

function pretty(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/**
 * Collapsible debug block for one tool call: name, input args, output, duration,
 * and a red error state if the tool failed. Collapsed by default.
 */
export default function ToolCallBlock({
  call,
  defaultOpen = false,
}: {
  call: ToolCallData;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isError = !!call.error;

  return (
    <div
      className={cn(
        "rounded-md border text-xs",
        isError
          ? "border-destructive/40 bg-destructive/5"
          : "border-border bg-muted/40",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        {isError ? (
          <AlertTriangle className="size-3.5 shrink-0 text-destructive" />
        ) : (
          <Wrench className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="font-medium">Tool call</span>
        <code className="rounded bg-background px-1 py-0.5 font-mono">
          {call.name}
        </code>
        {call.pending && <span className="text-muted-foreground">running…</span>}
        {isError && <span className="font-medium text-destructive">failed</span>}
        {typeof call.duration_ms === "number" && (
          <span className="ml-auto text-muted-foreground">
            {call.duration_ms}ms
          </span>
        )}
      </button>

      {open && (
        <div className="space-y-2 border-t px-2.5 py-2">
          <Section label="Input">{pretty(call.arguments)}</Section>
          {call.pending ? (
            <Section label="Output">
              <span className="text-muted-foreground">waiting for result…</span>
            </Section>
          ) : isError ? (
            <Section label="Error" tone="error">
              {call.error}
            </Section>
          ) : (
            <Section label="Output">{pretty(call.result)}</Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "error";
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <pre
        className={cn(
          "max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-background px-2 py-1.5 font-mono text-[11px] leading-relaxed",
          tone === "error" && "text-destructive",
        )}
      >
        {children}
      </pre>
    </div>
  );
}
